/**
 * Phase 9.7.22 — 🎧 Public Support & Feedback page
 * Route: /support
 *
 * Sections: hero, bug-report explainer, login help, upload help,
 * feature-suggestions, beta tester note, FAQ accordions, contact,
 * built-in feedback form (multipart — supports screenshot upload).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import SharedNavigation from "@/components/SharedNavigation";
import { useCurrentUser } from "@/lib/useCurrentUser";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const ISSUE_TYPES = [
  { value: "bug",                label: "🐞 Report a bug" },
  { value: "login",              label: "🔐 Login help" },
  { value: "upload",             label: "🎵 Upload issue" },
  { value: "gifting",            label: "🎁 Gifting / coins" },
  { value: "payouts",            label: "💸 Payouts" },
  { value: "feature_suggestion", label: "💡 Feature suggestion" },
  { value: "beta_feedback",      label: "🧪 Beta tester feedback" },
  { value: "other",              label: "❓ Something else" },
];

const SECTIONS = [
  { id: "bugs", emoji: "🐞", title: "Report Bugs", items: [
    "Play count or leaderboard looks off",
    "Upload fails or hangs",
    "Gifting / coin balance problems",
    "Live room / Beams bugs",
    "Profile, banner, or cover art issues",
  ]},
  { id: "login", emoji: "🔐", title: "Login Help", items: [
    "Password reset trouble",
    "Account access lost",
    "Verification / badge issues",
  ]},
  { id: "upload", emoji: "🎵", title: "Upload Issues", items: [
    "MP3 / WAV upload troubleshooting",
    "Cover art won't accept",
    "Tips for mobile uploads (iOS / Android)",
  ]},
  { id: "features", emoji: "💡", title: "Feature Suggestions", items: [
    "Ideas for new tools or rails",
    "Improvements to existing features",
    "Beta improvement requests",
  ]},
];

const FAQ = [
  {
    q: "Why are Reach and Plays different?",
    a: "▶ Plays = real listener plays only. 🏆 Reach = promotional exposure (RGM-driven boost + floor). 👁 Impressions = estimated discovery visibility (Reach × 8). These are three separate metrics — we never combine them into one number so your analytics stay honest.",
  },
  {
    q: "How do features work?",
    a: "When the A&R team features your track, RGM Studio applies a boosted reach pool (typically 15K-35K) over a 3-phase drip across 24-72 hours. Your track lands on the homepage Featured Push Today rail, in Discover rotation, and on Boost Pulse.",
  },
  {
    q: "How do gifting payouts work?",
    a: "Every coin gifted to you accrues in your wallet as earnings. You can withdraw via PayPal, Cash App, Zelle, Bank Transfer, or USDT crypto wallet once your balance crosses $25.",
  },
  {
    q: "How long do payouts take?",
    a: "Payouts are manually reviewed by RGM Studio for fraud protection. Standard turnaround is 3-7 business days. USDT/crypto payouts are typically same-day once approved.",
  },
  {
    q: "How do I become an A&R reviewer?",
    a: "A&R roles are invite-only based on community contribution, taste, and track record. The team reaches out directly when a slot opens.",
  },
  {
    q: "How do Spotlight boosts work?",
    a: "Every Sunday at 8pm EST, the Top 3 are crowned based on submission strength, engagement, and momentum. Spotlight winners get a real promotional push for the following week + a permanent spotlight badge.",
  },
];

const SupportPage = () => {
  const { currentUser } = useCurrentUser();
  const [form, setForm] = useState({
    name: "", email: "", issue_type: "bug", message: "",
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);

  // Phase 14.0.2 — admin-editable platform contact/brand settings.
  // Falls back to safe defaults if /platform/contact ever fails or
  // returns blanks. Reads NEVER block the page from rendering.
  const [platformContact, setPlatformContact] = useState({
    support_email: "support@rgmstudio.org",
    secondary_email: "",
    contact_blurb: "Prefer email? Reach the team directly at:",
    faq_markdown: "",
    social: { twitter: "", instagram: "", discord: "" },
    response_time_text: "We read every message and aim to respond within 48 hours.",
  });
  useEffect(() => {
    let cancelled = false;
    api.get("/platform/contact").then((r) => {
      if (cancelled || !r.data) return;
      setPlatformContact((prev) => ({
        support_email:      r.data.support_email      || prev.support_email,
        secondary_email:    r.data.secondary_email    || "",
        contact_blurb:      r.data.contact_blurb      || prev.contact_blurb,
        faq_markdown:       r.data.faq_markdown       || "",
        social: {
          twitter:   r.data.social?.twitter   || "",
          instagram: r.data.social?.instagram || "",
          discord:   r.data.social?.discord   || "",
        },
        response_time_text: r.data.response_time_text || prev.response_time_text,
      }));
    }).catch(() => {
      // silently fall back to defaults; never block the page
    });
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) { toast.error("Valid email required"); return; }
    if (!form.message.trim() || form.message.trim().length < 10) {
      toast.error("Please describe the issue (min 10 chars)");
      return;
    }
    if (file && file.size > 5 * 1024 * 1024) {
      toast.error("Screenshot too large (max 5MB)");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("email", form.email.trim());
      fd.append("issue_type", form.issue_type);
      fd.append("message", form.message.trim());
      if (typeof window !== "undefined") {
        fd.append("source_url", window.location.href);
      }
      if (file) fd.append("screenshot", file);
      const r = await api.post("/support/ticket", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmittedId(r.data?.ticket?.ticket_id || "received");
      setForm({ name: "", email: "", issue_type: "bug", message: "" });
      setFile(null);
      toast.success("Thanks — your ticket was received 🎧");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <SharedNavigation currentUser={currentUser} />

      <header className="max-w-4xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-8 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-200 text-[10px] uppercase tracking-[3px] font-black">
          🎧 RGM Support
        </div>
        <h1 className="mt-4 text-4xl sm:text-5xl font-black bg-gradient-to-r from-amber-300 via-pink-300 to-purple-300 bg-clip-text text-transparent">
          Support &amp; Feedback
        </h1>
        <p className="mt-3 text-amber-100/80 max-w-2xl mx-auto text-sm sm:text-base">
          Need help with uploads, login issues, gifting, features, or reporting bugs?
          Contact the RGM Studio team below.
        </p>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 pb-20 space-y-10">

        {/* Section cards */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="support-sections">
          {SECTIONS.map((s) => (
            <div
              key={s.id}
              className="p-5 rounded-2xl border border-amber-400/20 bg-gradient-to-br from-purple-900/10 to-black backdrop-blur"
              data-testid={`support-section-${s.id}`}
            >
              <p className="text-2xl">{s.emoji}</p>
              <p className="mt-2 text-white font-bold">{s.title}</p>
              <ul className="mt-2 text-[12px] text-amber-100/70 space-y-1">
                {s.items.map((it) => (
                  <li key={it} className="flex gap-2"><span className="text-amber-400">•</span><span>{it}</span></li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        {/* Beta tester note */}
        <section
          className="rounded-2xl border border-fuchsia-400/30 bg-gradient-to-br from-fuchsia-500/10 to-black p-5"
          data-testid="support-beta-note"
        >
          <p className="text-[10px] uppercase tracking-widest font-black text-fuchsia-300">
            🧪 Beta Tester Feedback
          </p>
          <p className="mt-2 text-amber-100/80 text-sm">
            RGM Studio is currently in active beta development. Your feedback
            helps shape the future of the platform. Every ticket lands directly
            on the founder's dashboard.
          </p>
        </section>

        {/* FAQ accordion */}
        <section data-testid="support-faq">
          <h2 className="text-xl sm:text-2xl font-black text-white">❓ FAQ</h2>
          <div className="mt-3 space-y-2">
            {FAQ.map((f, i) => {
              const open = openFaq === i;
              return (
                <div
                  key={f.q}
                  className={`rounded-xl border ${open ? "border-amber-400/50 bg-amber-500/5" : "border-amber-400/20 bg-black/40"} transition-colors`}
                  data-testid={`support-faq-item-${i}`}
                >
                  <button
                    className="w-full text-left p-4 flex items-center justify-between gap-3"
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    aria-expanded={open}
                    data-testid={`support-faq-toggle-${i}`}
                  >
                    <span className="text-white font-bold text-sm">{f.q}</span>
                    <span className={`text-amber-300 text-lg transition-transform ${open ? "rotate-45" : ""}`}>+</span>
                  </button>
                  {open && (
                    <div className="px-4 pb-4 text-sm text-amber-100/80 leading-relaxed" data-testid={`support-faq-answer-${i}`}>
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Contact */}
        <section
          className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/5 to-black p-5"
          data-testid="support-contact"
        >
          <h2 className="text-xl font-black text-white">📧 Contact</h2>
          <p
            className="mt-2 text-sm text-amber-100/80"
            data-testid="support-contact-blurb"
          >
            {platformContact.contact_blurb}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {platformContact.support_email && (
              <a
                href={`mailto:${platformContact.support_email}`}
                className="px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-200 text-xs font-bold hover:bg-amber-500/25"
                data-testid="support-email-support"
              >
                {platformContact.support_email}
              </a>
            )}
            {platformContact.secondary_email && (
              <a
                href={`mailto:${platformContact.secondary_email}`}
                className="px-3 py-1.5 rounded-full bg-purple-500/15 border border-purple-400/40 text-purple-200 text-xs font-bold hover:bg-purple-500/25"
                data-testid="support-email-secondary"
              >
                {platformContact.secondary_email}
              </a>
            )}
          </div>
          {/* Social links — only render when at least one is set */}
          {(platformContact.social.twitter ||
            platformContact.social.instagram ||
            platformContact.social.discord) && (
            <div
              className="mt-3 flex flex-wrap gap-2"
              data-testid="support-contact-socials"
            >
              {platformContact.social.twitter && (
                <a href={platformContact.social.twitter} target="_blank" rel="noreferrer"
                   className="px-3 py-1.5 rounded-full bg-sky-500/15 border border-sky-400/40 text-sky-200 text-xs font-bold hover:bg-sky-500/25"
                   data-testid="support-social-twitter">
                  𝕏 / Twitter
                </a>
              )}
              {platformContact.social.instagram && (
                <a href={platformContact.social.instagram} target="_blank" rel="noreferrer"
                   className="px-3 py-1.5 rounded-full bg-pink-500/15 border border-pink-400/40 text-pink-200 text-xs font-bold hover:bg-pink-500/25"
                   data-testid="support-social-instagram">
                  📸 Instagram
                </a>
              )}
              {platformContact.social.discord && (
                <a href={platformContact.social.discord} target="_blank" rel="noreferrer"
                   className="px-3 py-1.5 rounded-full bg-indigo-500/15 border border-indigo-400/40 text-indigo-200 text-xs font-bold hover:bg-indigo-500/25"
                   data-testid="support-social-discord">
                  💬 Discord
                </a>
              )}
            </div>
          )}
          {platformContact.response_time_text && (
            <p
              className="mt-3 text-[11px] text-amber-100/60 italic"
              data-testid="support-response-time"
            >
              {platformContact.response_time_text}
            </p>
          )}
          {platformContact.faq_markdown && (
            <div
              className="mt-4 p-3 rounded-lg bg-black/30 border border-amber-400/15 text-amber-100/85 text-xs whitespace-pre-line"
              data-testid="support-platform-faq"
            >
              {platformContact.faq_markdown}
            </div>
          )}
        </section>

        {/* Feedback form */}
        <section
          className="rounded-2xl border border-amber-400/40 bg-gradient-to-br from-black via-amber-900/5 to-black p-5 sm:p-6"
          data-testid="support-form-section"
        >
          <h2 className="text-xl font-black text-white">📝 Send us feedback</h2>
          <p className="mt-1 text-xs text-amber-200/70">
            All fields except screenshot are required. We read every submission.
          </p>

          {submittedId ? (
            <div
              className="mt-4 p-5 rounded-xl border border-emerald-400/40 bg-emerald-500/10 text-emerald-200 text-sm"
              data-testid="support-form-success"
            >
              ✅ Your ticket was received. Reference: <span className="font-mono">{submittedId}</span>
              <div className="mt-2 text-[12px] text-emerald-100/70">
                Want to submit another? <button onClick={() => setSubmittedId(null)} className="underline">Submit another ticket</button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-4 space-y-3" data-testid="support-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-amber-200">Name *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name or artist name"
                    data-testid="support-input-name"
                    className="bg-black/40 border-amber-400/30 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-amber-200">Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@example.com"
                    data-testid="support-input-email"
                    className="bg-black/40 border-amber-400/30 text-white mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-amber-200">Issue type *</Label>
                <select
                  value={form.issue_type}
                  onChange={(e) => setForm({ ...form, issue_type: e.target.value })}
                  data-testid="support-input-issue-type"
                  className="w-full bg-black/40 border border-amber-400/30 rounded-md px-3 py-2 text-white mt-1 text-sm"
                >
                  {ISSUE_TYPES.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs text-amber-200">Message *</Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us what happened, what you expected, and any track / submission IDs that help us debug."
                  rows={5}
                  data-testid="support-input-message"
                  className="bg-black/40 border-amber-400/30 text-white mt-1 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs text-amber-200">Screenshot (optional, max 5MB)</Label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="support-input-screenshot"
                  className="block mt-1 text-xs text-amber-100/70 file:mr-3 file:px-3 file:py-1.5 file:rounded-full file:border-0 file:bg-amber-500/20 file:text-amber-100 file:font-bold hover:file:bg-amber-500/30"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-400 hover:to-pink-400 text-white font-bold w-full sm:w-auto"
                data-testid="support-submit-btn"
              >
                {submitting ? "Sending…" : "🎧 Send to RGM"}
              </Button>
            </form>
          )}
        </section>

        <p className="text-center text-[10px] uppercase tracking-widest text-amber-300/40">
          RGM Studio · Beta · Built with love for artists
        </p>
      </main>
    </div>
  );
};

export default SupportPage;
